import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import StorefrontIcon from '@mui/icons-material/Storefront';
import tvTheme from './tvTheme';
import { TV_QR_IMAGE, TV_QR_URL } from '../../data/tvDemoDisplay';

const EYECATCH_SRC = '/assets/eyecatch1.png';

const actionSizes = (compact) => ({
  title: compact ? '0.95rem' : 'clamp(1.6rem, 2.85vw, 2.45rem)',
  weeklyCaption: compact ? '0.86rem' : 'clamp(1.32rem, 2.4vw, 2.05rem)',
  sectionHead: compact ? '0.84rem' : 'clamp(1.3rem, 2vw, 1.68rem)',
  body: compact ? '0.78rem' : 'clamp(1.2rem, 1.95vw, 1.58rem)',
  qrLead: compact ? '0.82rem' : 'clamp(1.35rem, 2.15vw, 2rem)',
  qrSub: compact ? '0.72rem' : 'clamp(1.12rem, 1.75vw, 1.62rem)',
  qrUrl: compact ? '0.58rem' : 'clamp(0.95rem, 1.4vw, 1.28rem)',
});

function LeafletSection({
  heading,
  icon,
  items,
  compact,
  headerBg,
  headerColor,
  accentBar = '#ff6b00',
  fill = false,
  maxItems = 2,
}) {
  const sizes = actionSizes(compact);
  const visible = items.slice(0, compact ? 2 : maxItems);

  return (
    <Box
      sx={{
        flex: fill ? 1 : undefined,
        flexShrink: fill ? undefined : 0,
        minHeight: fill ? 0 : undefined,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.35,
          px: compact ? 0.28 : 0.36,
          py: compact ? 0.18 : 0.24,
          background: headerBg,
          flexShrink: 0,
        }}
      >
        {icon}
        <Typography
          sx={{
            fontSize: sizes.sectionHead,
            fontWeight: 900,
            color: headerColor,
            lineHeight: 1.05,
          }}
        >
          {heading}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: fill ? 1 : undefined,
          minHeight: fill ? 0 : undefined,
          px: compact ? 0.3 : 0.38,
          py: compact ? 0.16 : 0.2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: compact ? 0.14 : 0.18,
          bgcolor: 'rgba(255,255,255,0.94)',
          overflow: 'auto',
        }}
      >
        {visible.map((text) => (
          <Typography
            key={text}
            sx={{
              fontSize: sizes.body,
              fontWeight: 800,
              color: '#1a1a1a',
              lineHeight: 1.24,
              textAlign: 'left',
              pl: compact ? 0.36 : 0.44,
              borderLeft: `4px solid ${accentBar}`,
            }}
          >
            {text}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

LeafletSection.propTypes = {
  heading: PropTypes.string.isRequired,
  icon: PropTypes.node,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  compact: PropTypes.bool,
  headerBg: PropTypes.string.isRequired,
  headerColor: PropTypes.string.isRequired,
  accentBar: PropTypes.string,
  fill: PropTypes.bool,
  maxItems: PropTypes.number,
};

function TVPromoCorner({ compact }) {
  const sizes = actionSizes(compact);

  if (compact) {
    return (
      <Box
        sx={{
          flexShrink: 0,
          m: 0,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        <Box
          component="img"
          src={EYECATCH_SRC}
          alt="目的ベースダッシュボードの紹介"
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 0.25,
            px: 0.2,
            py: 0,
            bgcolor: 'rgba(255,255,255,0.94)',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: sizes.qrLead, fontWeight: 900, lineHeight: 1.06 }}>
              こちらから無料で
            </Typography>
            <Typography sx={{ fontSize: sizes.qrLead, fontWeight: 900, lineHeight: 1.06 }}>
              お試しできます。
            </Typography>
            <Typography sx={{ fontSize: sizes.qrSub, fontWeight: 800 }}>（インストール不要）</Typography>
            <Typography component="a" href={TV_QR_URL} sx={{ fontSize: sizes.qrUrl, color: '#333' }}>
              {TV_QR_URL}
            </Typography>
          </Box>
          <Box component="img" src={TV_QR_IMAGE} alt="QRコード" sx={{ width: 44, height: 44, flexShrink: 0 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: '0 0 55%',
        minWidth: 0,
        height: '100%',
        m: 0,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          m: 0,
          p: 0,
          display: 'flex',
          alignItems: 'flex-end',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box
          component="img"
          src={EYECATCH_SRC}
          alt="目的ベースダッシュボードの紹介"
          sx={{
            width: '100%',
            maxWidth: '100%',
            height: 'auto',
            m: 0,
            p: 0,
            display: 'block',
            verticalAlign: 'bottom',
          }}
        />
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          m: 0,
          p: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.3,
          px: 0.2,
          py: 0,
          lineHeight: 0,
          bgcolor: 'rgba(255,255,255,0.94)',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            py: 0,
            lineHeight: 'normal',
          }}
        >
          <Typography sx={{ fontSize: sizes.qrLead, fontWeight: 900, color: '#111', lineHeight: 1.02, m: 0, p: 0 }}>
            こちらから無料で
          </Typography>
          <Typography sx={{ fontSize: sizes.qrLead, fontWeight: 900, color: '#111', lineHeight: 1.02, m: 0, p: 0 }}>
            お試しできます。
          </Typography>
          <Typography sx={{ fontSize: sizes.qrSub, fontWeight: 800, color: '#111', lineHeight: 1.02, m: 0, p: 0 }}>
            （インストール不要）
          </Typography>
          <Typography
            component="a"
            href={TV_QR_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: sizes.qrUrl,
              fontWeight: 600,
              color: '#333',
              lineHeight: 1.02,
              m: 0,
              p: 0,
              textDecoration: 'none',
              wordBreak: 'break-all',
            }}
          >
            {TV_QR_URL}
          </Typography>
        </Box>
        <Box
          component="img"
          src={TV_QR_IMAGE}
          alt="Webダッシュボード QRコード"
          sx={{
            flexShrink: 0,
            width: 120,
            height: 120,
            display: 'block',
            m: 0,
            p: 0,
            verticalAlign: 'middle',
          }}
        />
      </Box>
    </Box>
  );
}

TVPromoCorner.propTypes = {
  compact: PropTypes.bool,
};

function TVActionPanel({ data, compact = false, loading = false }) {
  const { panelTitle, weeklyCaption, forecast, actions } = data;
  const sizes = actionSizes(compact);
  const fillSections = !compact;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        m: 0,
        p: 0,
        display: 'flex',
        gap: compact ? 0.12 : 0.32,
        flexDirection: compact ? 'column' : 'row',
        alignItems: 'stretch',
        overflow: 'hidden',
        opacity: loading ? 0.5 : 1,
      }}
    >
      <Box
        sx={{
          flex: compact ? '1 1 auto' : '1 1 45%',
          minWidth: 0,
          minHeight: 0,
          m: 0,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flexShrink: 0, lineHeight: 1.06, m: 0, p: 0 }}>
          <Typography
            sx={{
              fontSize: sizes.title,
              fontWeight: 900,
              color: tvTheme.colors.textDark,
              m: 0,
            }}
          >
            {panelTitle}
          </Typography>
          {weeklyCaption && (
            <Typography
              sx={{
                fontSize: sizes.weeklyCaption,
                fontWeight: 900,
                color: tvTheme.colors.accentBlue,
                mt: compact ? 0.04 : 0.08,
                m: 0,
              }}
            >
              {weeklyCaption}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: compact ? 0.12 : 0.38,
            m: 0,
            p: 0,
            mt: compact ? 0.08 : 0.12,
          }}
        >
          <LeafletSection
            heading={forecast.heading}
            icon={<WbSunnyIcon sx={{ color: '#fff', fontSize: compact ? 19 : 32 }} />}
            items={forecast.items}
            compact={compact}
            fill={fillSections}
            headerBg="linear-gradient(90deg, #ff6b00 0%, #ff9800 100%)"
            headerColor="#fff"
            accentBar="#ff6b00"
          />
          <LeafletSection
            heading={actions.heading}
            icon={<StorefrontIcon sx={{ color: '#fff', fontSize: compact ? 19 : 32 }} />}
            items={actions.items}
            compact={compact}
            fill={fillSections}
            headerBg={`linear-gradient(90deg, ${tvTheme.colors.labelBar} 0%, #1976d2 100%)`}
            headerColor="#fff"
            accentBar={tvTheme.colors.labelBar}
          />
        </Box>
      </Box>

      <TVPromoCorner compact={compact} />
    </Box>
  );
}

TVActionPanel.propTypes = {
  data: PropTypes.object.isRequired,
  compact: PropTypes.bool,
  loading: PropTypes.bool,
};

export default TVActionPanel;
